<template lang='pug'>
  div
    v-toolbar(dense :flat='showFilters')
      v-tooltip(right v-if='selected.length')
        template(v-slot:activator='{on}')
          v-btn(v-on='on' @click='deleteSelected()' text icon color='error')
            v-icon delete
        span Delete
      v-spacer
      filter-button(v-model='showFilters' :badge='filterCount')
    v-expand-transition.ma-4
      v-card(v-show='showFilters' elevation='0')
        v-card-text
          v-row
            v-col(cols='12' sm='6')
              suite-filter(v-model='suite' label='Filter by Suite' @input='load')
            v-col(cols='12' sm='6')
              tags(v-model='tags' label='Filter by Tags')
          v-row
            v-col(cols='12' sm='6')
              status-filter(v-model='status' label='Filter by Status' @input='load')
            v-col(cols='12' sm='6')
              version-filter(v-model='targetVersion' label='Filter by Version' @input='load')
          v-row(v-if='gitMode')
            v-col(cols='12' sm='6')
              select-branch(v-model='branch' label='Filter by Branch' @input='load')
    table-list(:headers='headers' :items='results' :total='total' :init-options='tableOptions' :loading='loading' v-model='selected' @update='update' item-key='runGuid')
      template(v-slot:empty)
        v-container(v-if='!loading && !results.length')
          v-row
            v-col(align='center' justify='center')
              .text-h5.grey--text No Results Yet
      template(v-slot:row='{item, index, expanded}')
        // <tr> (Gets put inside tr)
        td
          router-link(:to='`/results/${item.runGuid}`') {{item.testSuiteName}}
        td
          bionic-metrics-test-status-indicator(v-if='!item.isRunning' :status='item.status')
          v-tooltip(bottom v-if='item.isRunning')
            template(v-slot:activator='{on}')
              bionic-metrics-running-indicator(v-on='on')
            span Running
        td
          v-tooltip(right)
            template(v-slot:activator='{on}')
              span(v-on='on') {{item.updatedAt | timeago}}
            span {{item.updatedAt | date}}
        td(v-if='gitMode') {{item.gitBranch}}
        td {{item.source}}
        td {{item.baseUrl}}
        td
          display-tags(v-model='item.tags' @tag='appendTag')
        td
          v-chip(x-small v-if='item.targetVersion' @click.stop.prevent='setTargetVersion(item.targetVersion)') {{item.targetVersion}}
        td
          v-row(justify='end')
            v-tooltip(bottom)
              template(v-slot:activator='{on}')
                v-btn(v-on='on' text icon :to='`/results/${item.runGuid}`')
                  v-icon remove_red_eye
              span View
            v-menu(offset-y left)
              template(v-slot:activator='{on}')
                v-btn(text icon v-on='on' @click.native.stop='')
                  v-icon more_vert
              v-list
                v-list-item(@click='deleteResult(item)')
                  v-list-item-title
                    | Delete
        // </tr> (Gets put inside tr)
      template(v-slot:item='{item}')
        v-col(no-gutters).text-body-1.pa-3
          v-row(no-gutters)
            v-col(cols='8')
              .text-subtitle-1 {{item.testSuiteName}}
            v-col(cols='4')
              v-row(justify='end' align='center')
                bionic-metrics-test-status-indicator.mr-1(v-if='!item.isRunning' :status='item.status')
                v-tooltip(bottom v-if='item.isRunning')
                  template(v-slot:activator='{on}')
                    bionic-metrics-running-indicator(v-on='on')
                  span Running
                v-tooltip(bottom)
                  template(v-slot:activator='{on}')
                    v-btn(v-on='on' text icon :to='`/results/${item.runGuid}`')
                      v-icon remove_red_eye
                  span View
                v-menu(offset-y left)
                  template(v-slot:activator='{on}')
                    v-btn.mr-1(text icon v-on='on' @click.native.stop='')
                      v-icon more_vert
                  v-list
                    v-list-item(@click='deleteResult(item)')
                      v-list-item-title
                        | Delete
          v-row(no-gutters)
            v-col.text-body-2(cols='3' sm='3') Base URL:
            v-col {{item.baseUrl}}
          v-row(no-gutters)
            v-col.text-body-2(cols='4' sm='3') Tags:
            v-col
              display-tags(v-model='item.tags' @tag='appendTag')
          v-row(no-gutters v-if='item.targetVersion')
            v-col.text-body-2(cols='3' sm='3') Version:
            v-col
              v-chip(x-small v-if='item.targetVersion' @click.stop.prevent='setTargetVersion(item.targetVersion)') {{item.targetVersion}}
          v-row(no-gutters)
            v-col.text-body-2(cols='3' sm='3') Updated:
            v-col
              v-tooltip(right)
                template(v-slot:activator='{on}')
                  span(v-on='on') {{item.updatedAt | date}}
                span {{item.updatedAt | timeago}}
          v-row(no-gutters)
            v-col.text-body-2(cols='3' sm='3') Source:
            v-col {{item.source}}
          v-row(no-gutters v-if='gitMode')
            v-col.text-body-2(cols='3' sm='3') Branch:
            v-col {{item.gitBranch}}
        v-divider
</template>

<script>
import _ from 'lodash';
import {mapGetters, mapMutations} from 'vuex';
import {createHelpers} from 'vuex-map-fields';
import Notifications from '../../services/notification.service';
import TableList from '../../components/TableList';
import BionicMetricsTestStatusIndicator from '../../components/TestStatusIndicator';
import BionicMetricsRunningIndicator from '../../components/RunningIndicator';
import TableMixin from '../../mixins/table.mixin';
import BionicMetricsEnvironments from '../../components/Environments';
import FilterButton from '../../components/FilterButton';
import Tags from '../../components/Tags';
import DisplayTags from '../../components/DisplayTags';
import SuiteFilter from '../../components/SuiteFilter';
import StatusFilter from '../../components/StatusFilter';
import VersionFilter from '../../components/VersionFilter';
import SelectBranch from '../../components/SelectBranch';
import TestRun from '../../api/testRun';

// We're using the same getter and mutation types
// as we've used in the store above.
const {mapFields} = createHelpers({
  getterType: 'getResultField',
  mutationType: 'mutateResultField'
});

export default {
  components: {
    SelectBranch,
    VersionFilter,
    StatusFilter,
    SuiteFilter,
    DisplayTags,
    Tags,
    FilterButton,
    BionicMetricsEnvironments,
    BionicMetricsRunningIndicator,
    BionicMetricsTestStatusIndicator,
    TableList
  },
  mixins: [TableMixin],
  name: 'BionicMetricsResults',
  computed: {
    targetVersion() {
      return this.filter.targetVersion ? _.trim(this.filter.targetVersion) : null;
    },
    filterStatus() {
      return this.filter.status ? this.filter.status.value : null;
    },
    suiteCount() {
      return this.filter.suite ? 1 : 0;
    },
    tagCount() {
      return this.filter.tags.length;
    },
    statusCount() {
      return this.filter.status ? 1 : 0;
    },
    versionCount() {
      return this.filter.targetVersion ? 1 : 0;
    },
    filterCount() {
      return this.tagCount + this.suiteCount + this.statusCount + this.versionCount;
    },
    suiteGuid() {
      return this.filter.suite ? this.filter.suite.guid : null;
    },
    gitMode() {
      return !!this.account.gitMode;
    },
    headers() {
      const defaultHeaders = [
        {text: 'Name', value: 'testSuiteName'},
        {text: 'Status', value: 'status'},
        {text: 'Updated', value: 'updatedAt'},
        {text: 'Source', value: 'source'},
        {text: 'URL', value: 'baseUrl'},
        {text: 'Tags', value: 'tags', sortable: true},
        {text: 'Version', value: 'targetVersion'},
        {text: '', value: 'actions', sortable: false}
      ];
      if (this.gitMode) defaultHeaders.splice(3, 0, {text: 'Branch', value: 'gitBranch'});
      return defaultHeaders;
    },
    ...mapGetters({
      user: 'getUser',
      account: 'getAccount',
      tableOptions: 'getResultTableOptions',
      filter: 'getResultFilter'
    }),
    ...mapFields({
      branch: 'filter.branch',
      tags: 'filter.tags',
      suite: 'filter.suite',
      status: 'filter.status',
      targetVersion: 'filter.targetVersion'
    })
  },
  methods: {
    to(route) {
      this.$router.push(route);
    },
    async removeResult(guid) {
      await TestRun.remove(guid);
      const i = this.results.findIndex((r) => r.guid === guid);
      this.results.splice(i, 1);
    },
    async deleteResult(result) {
      if (!(await Notifications.confirm('Delete', 'Are you sure you want to delete this result?'))) return;
      await this.removeResult(result.guid);
      Notifications.toast('Deleted');
    },
    async deleteSelected() {
      if (!(await Notifications.confirm('Delete', `Are you sure you want to delete <strong>${this.selected.length}</strong> results?`))) return;
      const all = [];
      _.each(this.selected, (result) => {
        all.push(this.removeResult(result.guid));
      });
      await Promise.all(all);
      this.selected = [];
      Notifications.toast('Deleted');
    },
    async setTargetVersion(targetVersion) {
      this.setResultTargetVersion(targetVersion);
      this.showFilters = true;
      await this.load();
    },
    async load() {
      this.loading = true;
      try {
        const projection = {
          testSuiteName: 1,
          guid: 1,
          runGuid: 1,
          status: 1,
          updatedAt: 1,
          source: 1,
          baseUrl: 1,
          tags: 1,
          targetVersion: 1,
          gitBranch: 1
        };
        const {list, total} = await TestRun.list({
          skip: this.skip,
          limit: this.limit,
          sort: this.sort,
          search: this.search,
          tags: this.filter.tags,
          suite: this.suiteGuid,
          status: this.filterStatus,
          targetVersion: this.targetVersion,
          branch: this.branch,
          projection
        });
        this.results = list;
        this.total = total;
      } catch (error) {
        console.error(`Problem getting list of results: ${error.message}`);
        Notifications.errorToast('Error Loading Results', {duration: 30000, retry: this.load});
      }
      this.loading = false;
    },
    ...mapMutations({
      setTableOptions: 'setResultTableOptions',
      setFilter: 'setResultFilter',
      appendTag: 'appendResultTag',
      setResultTargetVersion: 'setResultTargetVersion'
    })
  },
  watch: {
    // eslint-disable-next-line
    tagCount: _.debounce(function () {
      this.load();
    }, 250)
  },
  data() {
    return {
      selected: [],
      results: [],
      total: 0,
      skip: 0,
      limit: 10,
      sort: null,
      loading: false,
      showFilters: false
    };
  }
};
</script>

<style scoped>
</style>
